import { isBreak, type GameState, type PointEvent } from "./types";

/**
 * How often the receiving team is expected to hold, before this game has said
 * anything. Roughly what competitive ultimate runs at; the observed rate pulls
 * it around as points accumulate.
 */
const HOLD_PRIOR = 0.7;

/**
 * How many points of evidence the prior is worth.
 *
 * At 2–1 the observed hold rate is 33% or 100% and means nothing, and a model
 * that believed it would swing wildly for the first ten points of every game.
 * Twelve is enough that the number only starts moving once the game has
 * actually shown a pattern.
 */
const PRIOR_WEIGHT = 12;

/** Beyond these the sample is telling a story the model should not repeat. */
const HOLD_MIN = 0.4;
const HOLD_MAX = 0.85;

/**
 * How often the team receiving the pull goes on to score, learned from this
 * game and shrunk toward a prior.
 *
 * Per GAME rather than per tournament on purpose: a windy afternoon on an
 * exposed pitch breaks far more than a still one indoors, and the two are
 * happening at the same event. `isBreak` already knows which points were
 * holds.
 */
export function holdRate(points: PointEvent[]): number {
  let holds = 0;
  for (const p of points) if (!isBreak(p)) holds += 1;
  const rate = (holds + HOLD_PRIOR * PRIOR_WEIGHT) / (points.length + PRIOR_WEIGHT);
  return Math.min(HOLD_MAX, Math.max(HOLD_MIN, rate));
}

/**
 * Win probability for team A, as a fraction 0..1.
 *
 * Ultimate is a race to a target score, so the model asks: how likely is a team
 * to win the remaining race? Each remaining point goes to whoever is RECEIVING
 * with probability `holdRate`, and possession alternates — whoever scores pulls
 * next.
 *
 * That possession term is the whole reason this is not a coin flip. At 14–14 to
 * 15 the two teams do not have equal chances: the team receiving is about to
 * play the last point on offence, and saying 50/50 there is the model being
 * wrong in the one moment everybody is watching it.
 *
 * It stays deliberately blind to TEAM STRENGTH. No head-to-head, no seeding, no
 * record. A weekend tournament gives one or two prior meetings between any two
 * teams, which is noise, and a public number that buries a team for losing once
 * before is both unfair and visibly so. This reads as "how safe is this lead,
 * and who has the disc" — which is answerable — and never claims certainty
 * until the game is mathematically over.
 */
export function winProbabilityA(
  state: Pick<GameState, "teamA" | "teamB" | "config" | "period" | "offense" | "points">,
): number | null {
  if (state.period === "pregame") return null;
  if (state.period === "final") {
    // A timed game can genuinely end level; claiming 100% for either side lies.
    if (state.teamA.score === state.teamB.score) return 0.5;
    return state.teamA.score > state.teamB.score ? 1 : 0;
  }

  const target = state.config.scoreTarget;
  const a = state.teamA.score;
  const b = state.teamB.score;

  // Time-only formats have no finish line to race to; a lead can't be
  // converted into a probability without knowing how many points remain.
  if (target === null) return null;

  if (a >= target) return 1;
  if (b >= target) return 0;

  const needA = target - a;
  const needB = target - b;
  // No possession recorded (a game saved before point tracking, or a field
  // nobody has set the pull on): `hold` of a half is exactly the coin flip this
  // model used to be, so the old behaviour is the graceful fallback rather than
  // a special case.
  const offense = state.offense;
  const hold = offense === undefined ? 0.5 : holdRate(state.points ?? []);
  return raceProbability(needA, needB, offense ?? "A", hold);
}

/**
 * Probability the side needing `needA` more points gets there first, walking the
 * race point by point with possession alternating.
 *
 * Solved exactly rather than simulated, so the number is stable frame to frame:
 * a win probability that jitters while nothing is happening reads as broken.
 * The state space is (points A needs) x (points B needs) x (who receives), which
 * is a few hundred cells at any real score target.
 */
function raceProbability(
  needA: number,
  needB: number,
  offense: "A" | "B",
  hold: number,
): number {
  const memo = new Map<string, number>();

  const walk = (a: number, b: number, off: "A" | "B"): number => {
    if (a <= 0) return 1;
    if (b <= 0) return 0;
    const key = `${a},${b},${off}`;
    const seen = memo.get(key);
    if (seen !== undefined) return seen;
    // The receiving team holds with `hold`; the other side breaks otherwise.
    const pA = off === "A" ? hold : 1 - hold;
    // Whoever scores pulls, so the other team receives the next point.
    const out = pA * walk(a - 1, b, "B") + (1 - pA) * walk(a, b - 1, "A");
    memo.set(key, out);
    return out;
  };

  return walk(needA, needB, offense);
}

/** Rounded percentage for display, clamped so it never shows a bare 0% or 100% mid-game. */
export function displayPercent(p: number, decided: boolean): number {
  const pct = Math.round(p * 100);
  if (decided) return pct;
  return Math.min(99, Math.max(1, pct));
}

/**
 * The same model after every point of a FINISHED game, for the story a
 * result page tells: team A's chance, 0..1, before the first point and after
 * each one. `target` is the score the game was raced to - the winner's final
 * score when the record carries nothing better - so the last entry is 1 or 0
 * and the first is the model's opening view, which knows only who received.
 * The hold rate at each step is what the game had shown BY THEN, exactly as
 * the live number moved on the day.
 */
export function winProbabilitySeries(points: readonly PointEvent[], target: number): number[] {
  const out: number[] = [];
  const first = points[0];
  const opening = first === undefined ? 0.5 : holdRate([]);
  if (first === undefined || target < 1) return [0.5];
  out.push(raceProbability(target, target, first.offense, opening));
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    if (p === undefined) break;
    if (p.scoreA >= target) {
      out.push(1);
      continue;
    }
    if (p.scoreB >= target) {
      out.push(0);
      continue;
    }
    // Whoever scored pulls: the other side receives the next point.
    const offenseNext: "A" | "B" = p.team === "A" ? "B" : "A";
    out.push(
      raceProbability(target - p.scoreA, target - p.scoreB, offenseNext, holdRate(points.slice(0, i + 1))),
    );
  }
  return out;
}
